import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title } from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title);

// Reusable component for top statistic cards
const StatCard = ({ title, value, icon, color, trend, trendValue }) => (
  <div className="col">
    <div className="card border-start-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body">
        <div className="row no-gutters align-items-center">
          <div className="col me-2">
            <div className="text-xs fw-bold text-uppercase mb-1" style={{ color }}>{title}</div>
            <div className="h5 mb-0 fw-bold text-gray-800">{value}</div>
            {trend && (
              <div className={`text-xs mt-1 ${trend === 'up' ? 'text-success' : 'text-danger'}`}>
                <i className={`fas fa-arrow-${trend} me-1`}></i>
                {trendValue}%
              </div>
            )}
          </div>
          <div className="col-auto">
            <i className={`fas ${icon} fa-2x text-gray-300`}></i>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30days');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [pandasData, setPandasData] = useState(null);
  const [answerStats, setAnswerStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsRes, pandasRes, answersRes] = await Promise.all([
        api.get(`/statistics/analytics?timeRange=${timeRange}`),
        api.get('/statistics/pandas-summary'),
        api.get('/statistics/answers')
      ]);

      if (analyticsRes.data.success) setAnalyticsData(analyticsRes.data);
      else throw new Error('Failed to load main analytics');
      
      if (pandasRes.data.success) setPandasData(pandasRes.data.data);
      else throw new Error('Failed to load summary data');

      if (answersRes.data.success) setAnswerStats(answersRes.data);
      else throw new Error('Failed to load answer statistics');

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'An error occurred while loading dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = (title, scales) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: title, font: { size: 16 } },
    },
    scales: scales || {}
  });

  const renderCharts = () => {
    if (!analyticsData || !pandasData || !answerStats) return null;

    // Data for Health Trends Chart
    const healthTrendsData = {
      labels: analyticsData.healthTrends.labels,
      datasets: [
        {
          label: 'Average Score',
          data: analyticsData.healthTrends.datasets.averageScore,
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78, 115, 223, 0.1)',
          yAxisID: 'y',
          fill: true,
        },
        {
          label: 'At Risk %',
          data: analyticsData.healthTrends.datasets.atRiskPercentage,
          borderColor: '#e74a3b',
          backgroundColor: 'rgba(231, 74, 59, 0.1)',
          yAxisID: 'y1',
          fill: true,
        },
      ],
    };

    // Data for Health Distribution Chart
    const healthDistributionData = {
      labels: ['Excellent', 'Good', 'Fair', 'At Risk'],
      datasets: [{
        data: [
          analyticsData.healthDistribution.excellent,
          analyticsData.healthDistribution.good,
          analyticsData.healthDistribution.fair,
          analyticsData.healthDistribution.atRisk
        ],
        backgroundColor: ['#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b'],
      }]
    };

    // Data for Gender Distribution Chart
    const genderData = {
        labels: Object.keys(analyticsData.demographicData.genderDistribution),
        datasets: [{
            data: Object.values(analyticsData.demographicData.genderDistribution),
            backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e'],
        }]
    };

    // Data for Age Group Chart
    const ageGroupData = {
        labels: Object.keys(analyticsData.demographicData.ageGroups),
        datasets: [{
            label: 'Percentage',
            data: Object.values(analyticsData.demographicData.ageGroups),
            backgroundColor: 'rgba(28, 200, 138, 0.7)',
        }]
    };

    // Data for Protocol Completion Chart
    const protocolCompletionData = {
        labels: analyticsData.protocolCompletion.labels,
        datasets: [{
            label: 'Completion Rate (%)',
            data: analyticsData.protocolCompletion.completionRates,
            backgroundColor: 'rgba(246, 194, 62, 0.7)',
        }]
    };

    // Data for Response Rate Chart
    const responseRateData = {
        labels: Array.from({ length: analyticsData.responseRate.byDay.length }, (_, i) => `Day ${i + 1}`),
        datasets: [{
            label: 'Daily Response Rate (%)',
            data: analyticsData.responseRate.byDay,
            borderColor: '#36b9cc',
            tension: 0.1
        }]
    };

    return (
      <>
        {/* Key Metrics Row */}
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-4 g-4 mb-4">
          <StatCard title="Total Assessments" value={analyticsData.keyMetrics.totalAssessments} icon="fa-file-alt" color="#4e73df" />
          <StatCard title="Completed Cases" value={analyticsData.keyMetrics.completedCases} icon="fa-check-circle" color="#1cc88a" />
          <StatCard title="Average Score" value={`${analyticsData.keyMetrics.averageScore} / 100`} icon="fa-star-half-alt" color="#f6c23e" />
          <StatCard title="Total Responses" value={answerStats.totalResponses} icon="fa-comments" color="#5a5c69" />
        </div>

        {/* Health Trends & Distribution */}
        <div className="row">
          <div className="col-xl-8 col-lg-7">
            <div className="card shadow-sm mb-4">
              <div className="card-body" style={{ height: '400px' }}>
                <Line options={chartOptions('Health Score & At-Risk Trend', { y: { type: 'linear', position: 'left' }, y1: { type: 'linear', position: 'right', grid: { drawOnChartArea: false } } })} data={healthTrendsData} />
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-5">
            <div className="card shadow-sm mb-4">
              <div className="card-body" style={{ height: '400px' }}>
                <Doughnut options={chartOptions('Health Status Distribution')} data={healthDistributionData} />
              </div>
            </div>
          </div>
        </div>

        {/* Demographics & Risk Factors */}
        <div className="row">
          <div className="col-lg-3">
            <div className="card shadow-sm mb-4">
              <div className="card-body" style={{ height: '350px' }}>
                <Doughnut options={chartOptions('Gender Distribution')} data={genderData} />
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card shadow-sm mb-4">
              <div className="card-body" style={{ height: '350px' }}>
                <Bar options={chartOptions('Age Group Distribution (%)')} data={ageGroupData} />
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card shadow-sm mb-4">
              <div className="card-header py-3"><h6 className="m-0 fw-bold text-primary">Top Risk Factors</h6></div>
              <div className="card-body">
                {analyticsData.riskFactors.map(risk => (
                  <div key={risk.factor}>
                    <h4 className="small fw-bold">{risk.factor} <span className="float-end">{risk.percentage}%</span></h4>
                    <div className="progress mb-4" style={{height: '12px'}}>
                      <div className="progress-bar" role="progressbar" style={{ width: `${risk.percentage}%`, backgroundColor: `rgba(231, 74, 59, ${risk.impact / 10})` }} aria-valuenow={risk.percentage} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Protocol & Response Analysis */}
        <div className="row">
            <div className="col-lg-6">
                <div className="card shadow-sm mb-4">
                    <div className="card-body" style={{ height: '400px' }}>
                        <Bar options={chartOptions('Protocol Completion Rates (%)')} data={protocolCompletionData} />
                    </div>
                </div>
            </div>
            <div className="col-lg-6">
                <div className="card shadow-sm mb-4">
                    <div className="card-body" style={{ height: '400px' }}>
                        <Line options={chartOptions('Response Rate Over Time')} data={responseRateData} />
                    </div>
                </div>
            </div>
        </div>
      </>
    );
  };

  return (
    <ResearchLayout pageTitle="Analytics Dashboard">
      <div className="container-fluid">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800">Analytics Dashboard</h1>
          <div className="d-flex align-items-center">
            <select className="form-select w-auto me-2" value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="1year">Last 1 Year</option>
            </select>
            <button className="btn btn-primary" onClick={loadDashboardData} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <i className="fas fa-sync-alt"></i>}
            </button>
          </div>
        </div>
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        {loading ? (
          <div className="text-center p-5"><div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status"><span className="visually-hidden">Loading...</span></div></div>
        ) : analyticsData ? (
          renderCharts()
        ) : (
          !loading && <div className="alert alert-info mt-3">No data available to display for the selected time range.</div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default AnalyticsPage;

const styles = `
  .chart-container {
    background-color: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .chart-header {
    margin-bottom: 1.25rem;
  }

  .chart-area {
    height: 200px;
    position: relative;
  }

  .chart-columns {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    height: 100%;
  }

  .chart-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    height: 100%;
  }

  .chart-column:hover .chart-tooltip {
    opacity: 1;
  }

  .chart-tooltip {
    position: absolute;
    top: -40px;
    background-color: rgba(0,0,0,0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1000;
    text-align: center;
    pointer-events: none;
  }

  .chart-label {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #6c757d;
  }

  .bar-primary {
    width: 20px;
    background-color: #007bff;
    border-radius: 4px 4px 0 0;
  }

  .bar-danger {
    width: 20px;
    background-color: #dc3545;
    border-radius: 4px 4px 0 0;
  }

  .bar-success {
    width: 20px;
    background-color: #28a745;
    border-radius: 4px 4px 0 0;
  }

  .legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 5px;
  }

  .donut-chart-container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
  }

  .donut-chart {
    width: 200px;
    height: 200px;
    position: relative;
    border-radius: 50%;
    margin-right: 2rem;
  }

  .donut-hole {
    position: absolute;
    width: 130px;
    height: 130px;
    background-color: white;
    border-radius: 50%;
    top: 35px;
    left: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .donut-hole-text .large {
    font-size: 1.5rem;
    font-weight: 700;
    color: #343a40;
  }

  .donut-hole-text .small {
    font-size: 0.75rem;
    color: #6c757d;
  }

  .donut-segment {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    clip: rect(0px, 200px, 200px, 100px);
  }

  .donut-segment::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    clip: rect(0px, 100px, 200px, 0px);
    transform: rotate(calc(3.6deg * var(--offset)));
    transform-origin: center right;
  }

  .donut-segment.excellent::before {
    background-color: #28a745;
  }

  .donut-segment.good::before {
    background-color: #17a2b8;
  }

  .donut-segment.fair::before {
    background-color: #ffc107;
  }

  .donut-segment.at-risk::before {
    background-color: #dc3545;
  }

  .legend-dot.excellent {
    background-color: #28a745;
  }

  .legend-dot.good {
    background-color: #17a2b8;
  }

  .legend-dot.fair {
    background-color: #ffc107;
  }

  .legend-dot.at-risk {
    background-color: #dc3545;
  }

  .legend-item {
    margin-bottom: 0.5rem;
    white-space: nowrap;
  }

  .donut-legend {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }
`;